import { useAuthStore } from '~/stores/auth'

/**
 * This composable provides backward-compatibility with the previous useAuth.js implementation
 * while leveraging the new Pinia store underneath.
 * 
 * It allows for a gradual migration of components that use the old API.
 * 
 * SECURITY NOTE: This composable uses getUser() for authentication rather than
 * relying on potentially insecure session data. Always use getUser() to verify
 * the user's authentication status.
 */
export default function useAuth() {
  const authStore = useAuthStore()
  
  // Initialize the auth store if not already initialized
  if (!authStore.isInitialized) {
    try {
      authStore.initialize().catch(() => {
        // Ensure loading state is reset if initialization fails
        if (authStore.loading) {
          authStore.$patch({ loading: false })
        }
      })
    } catch (err) {
      // Fallback to reset loading state in case of synchronous errors
      authStore.$patch({ loading: false })
    }
  }
  
  // Create a wrapped version of initialize that ensures loading state is reset
  const safeInitialize = async () => {
    try {
      await authStore.initialize()
    } catch (err) {
      // Reset loading state on error
      authStore.$patch({ loading: false })
      throw err
    }
  }
  
  return {
    // Provide readonly references to store values
    get user() { return authStore.user },
    get loading() { return authStore.loading },
    get error() { return authStore.error },
    get isAuthenticated() { return authStore.isAuthenticated },
    
    // Map old methods to new store actions with safety wrappers
    initialize: safeInitialize,
    checkSession: authStore.checkSession,
    forceUpdateAuthState: authStore.forceUpdateAuthState,
    login: authStore.login,
    signup: authStore.signup,
    loginWithGoogle: authStore.loginWithGoogle,
    logout: authStore.logout,
    resetPassword: authStore.resetPassword,
    updatePassword: authStore.updatePassword
  }
} 